import PropTypes from 'prop-types';
import React from 'react';
import { Dropdown, SelectButton, Icon, Tooltip, FilterSetController, Filter, ToastContext, Card } from '@weezevent/nacre';
import { isMobile } from 'react-device-detect';

import RateModel from '../../../models/rateModel';
import RateGroup from '../../../models/rateGroup';
import Inventory from '../../../models/inventory';
import Category from '../../../models/category';
import Contingent from '../../../models/contingentModel';

import { Loader } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import RatesTable from './../ratesTable';
import './../../../pages/rates/rates.css';
import Style from './rateManager.module.css';

import * as Sentry from '@sentry/browser';
import { ContingentAction } from './../components/action/contingentAction';
import { InventoryAction } from './../components/action/inventoryAction';
import { CategoryAction } from './../components/action/categoryAction';
import { GroupAction } from './../components/action/groupAction';
import { ModifyAction } from './../components/action/modifyAction';
import { DeleteAction } from './../components/action/deleteAction';
import { DuplicateAction } from './../components/action/duplicateAction';
import { GenerateTemplateFromRateAction } from './../components/action/generateTemplateFromRateAction';
import { ModifyTemplateAction } from './../components/action/modifyTemplateAction';
import ModalSortRates from './modalSortRates';
import { useSegment } from '../../../components/tracking/trackingProvider';
import { SeeAttendeesAction } from './action/seeAttendeesAction';

export function generateClassNameRateName(rate, groups) {
    if (rate.group) {
        const rateGroup = groups.find(group => group.pk === rate.group);
        if (rateGroup.parent) {
            return 'padding-rate-with-sub-group';
        }

        return 'padding-rate-with-group';
    }

    return '';
}

export function SortRatesButton({ disabled = false, tooltip = null, label = null, onClick = () => {}, icon = null }) {
    const { t } = useTranslation();
    if (isMobile) {
        return null;
    }

    return (
        <div>
            <Tooltip className={'secondary-action-container'} visible={disabled} margin={false} triggerOnHover title={tooltip ? tooltip : t('ticket.rates.sortRates.tooltipLabel')}>
                <div className={`${'secondary-action-edit'} ${disabled ? 'secondary-action-edit-disabled' : ''}`} onClick={!disabled ? onClick : null}>
                    <span className={'secondary-action-edit-label'}>{label}</span>
                    <Icon name={icon || 'list'} className={'secondary-action-edit-icon'} />
                </div>
            </Tooltip>
        </div>
    );
}

export function SwitcherAllGroups({ groups = [], onChangeGroups = () => {} }) {
    const [expandAll, setExpandAll] = React.useState(true);
    const { t } = useTranslation();
    const { track } = useSegment();

    const switchAllGroups = React.useCallback(() => {
        let _expandAll = !expandAll;

        onChangeGroups(currentState => {
            let newState = [...currentState];
            newState.map(group => {
                group.showRates = _expandAll;
                return group;
            });
            return newState;
        });
        setExpandAll(_expandAll);
        track('ACTION > COLLAPSE GROUPS', { 'Page Title': 'TICKET TYPES' });
    }, [groups, onChangeGroups, expandAll]);

    if (!groups.length) {
        return null;
    }

    return (
        <div className={'secondary-action-container'}>
            <div className={'secondary-action-edit'} onClick={switchAllGroups}>
                <span className={'secondary-action-edit-label'}>{expandAll ? t('ticket.rates.collapseGroups') : t('ticket.rates.expandGroups')}</span>
                <Icon name={expandAll ? 'folder-closed' : 'folder-open'} className={'secondary-action-edit-icon'} />
            </div>
        </div>
    );
}

export default function RatesManager({
    event,
    organization,
    tableCells = [],
    initialQuery,
    queryParams = {},
    extraRates = [],
    excludeRatesIds = [],
    onGroupsLoaded = () => {},
    onRatesLoaded = () => {},
    onReloadList = () => {},
    selected = null,
    onRatesSelected = () => {},
    mode = 'rate-list',
    hideEmptyGroup = false,
    children = null,
    selectable = true,
    activeSyncRates = true,
    activeCacheRates = false,
    activeCacheGroups = false,
    quotaTakenByRate = {},
    quotaTakenByGroup = {},
    settings = null
}) {
    const { t } = useTranslation();
    const { track } = useSegment();

    const [apiRates, setRates] = React.useState([]);
    const [openModalSort, setOpenModalSort] = React.useState(false);

    const rates = React.useMemo(() => {
        return [...apiRates, ...extraRates].filter(rate => !excludeRatesIds.includes(rate.pk)).sort((r1, r2) => r1.display_order - r2.display_order);
    }, [apiRates, extraRates, excludeRatesIds]);

    const [groups, setGroups] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [contingents, setContingents] = React.useState([]);
    const [inventories, setInventories] = React.useState([]);
    const [_selectedRates, _setSelected] = React.useState([]);
    const [selectedGroups, setSelectedGroups] = React.useState([]);
    const [allSelected, setAllSelected] = React.useState(false);
    const [filters, setFilters] = React.useState({});
    const [inputSearchValue, setInputSearchValue] = React.useState('');

    const [isLoading, setLoading] = React.useState(true);
    const [isTableLoading, setTableLoading] = React.useState(false);

    const toast = React.useContext(ToastContext);

    const organizationId = organization.id;

    // useMemo to handle retrocompatiblilty for older pages.
    // allows us to pass a selectedRates prop instead of using the internal state
    const selectedRates = React.useMemo(() => selected || _selectedRates, [selected, _selectedRates]);

    const isSeated = React.useMemo(() => {
        return event && event.isSeatedForRates();
    }, [event]);

    const setSelected = React.useCallback(
        selectedRates => {
            _setSelected(selectedRates);
            onRatesSelected(selectedRates);
        },
        [onRatesSelected]
    );

    let handleSearchQuery = (value, filters) => {
        let query = {};
        if (value !== '') {
            query.search = value;
        }
        if (filters.inventories?.size > 0) {
            query.inventories = Array.from(filters?.inventories).join(',');
        }
        if (filters.categories?.size > 0) {
            query.categories = Array.from(filters?.categories).join(',');
        }
        if (filters.groups?.size > 0) {
            query.groups = Array.from(filters?.groups).join(',');
        }
        if (filters.contingents?.size > 0) {
            query.contingents = Array.from(filters?.contingents).join(',');
        }
        if ('visibility' in filters) {
            query.visibility = filters.visibility;
        }

        return query;
    };

    const reloadRates = React.useCallback(
        (value, filters) => {
            setTableLoading(true);
            let rateElements = RateModel.getAll({
                ...initialQuery,
                query: {
                    // sync: activeSyncRates ? 'True' : 'False',
                    ...queryParams,
                    expand: 'relays',
                    ...handleSearchQuery(value, filters),
                    limit: 1000
                }
            });

            rateElements
                .then(rateElements => {
                    RateModel.sort(rateElements);
                    setRates(rateElements);
                })
                .catch(e => {
                    Sentry.captureException(e);
                    toast.error(e);
                })
                .finally(() => {
                    setTableLoading(false);
                });
        },
        [organizationId, event]
    );

    const reloadList = React.useCallback(
        (sync = true) => {
            setLoading(true);
            setFilters({});

            // Ask pyvar to sync with weez: we can be sure of having all rates up to date
            let rateElements = RateModel.getAll({
                ...initialQuery,
                cache: activeCacheRates,
                allow_cache: activeCacheRates,
                query: {
                    // sync: sync && activeSyncRates ? 'True' : 'False',
                    ...queryParams,
                    expand: 'relays',
                    limit: 1000
                }
            });

            let rateGroups = RateGroup.getAll({
                organizationId,
                eventId: event.id,
                cache: activeCacheGroups,
                allow_cache: activeCacheGroups
            });

            let categoryPromise = isSeated
                ? Category.getAll({
                      organizationId,
                      chartId: event.chart
                  })
                : null;
            let contingentPromise = isSeated
                ? Contingent.getAll({
                      organizationId,
                      eventId: event.id
                  })
                : null;
            let inventoryPromise = isSeated
                ? Inventory.getAll({
                      organizationId,
                      chartId: event.chart
                  })
                : null;

            Promise.all([rateElements, rateGroups, categoryPromise, inventoryPromise, contingentPromise])
                .then(([rateElements, ratesGroup, categoriesElements, inventoriesElements, contingentsElements]) => {
                    //Sort rate and group with display_order
                    RateModel.sort(rateElements);
                    RateGroup.sort(ratesGroup);
                    setRates(rateElements || []);
                    onRatesLoaded(rateElements || []);
                    onReloadList(reloadList);
                    ratesGroup.forEach(group => (group.showRates = true));
                    setGroups(ratesGroup || []);
                    onGroupsLoaded(ratesGroup || []);
                    setCategories(categoriesElements || []);
                    let tmpInventories = isSeated ? inventoriesElements.filter(inventory => inventory.event === event.id) : null;
                    setInventories(tmpInventories || []);
                    setContingents(contingentsElements || []);
                })
                .catch(e => {
                    Sentry.captureException(e);
                    toast.error(e);
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [onGroupsLoaded, onRatesLoaded, onReloadList, activeCacheGroups, activeCacheRates, activeSyncRates, isSeated]
    );

    const reloadAndResetSelected = React.useCallback(() => {
        reloadList();
        _setSelected([]);
        setSelectedGroups([]);
    }, [reloadList]);

    React.useEffect(() => {
        reloadList();
    }, []);

    const selectRate = React.useCallback(
        rate => {
            let newSelected = selectedRates.slice(0);
            if (newSelected.find(selected => selected.id === rate.id)) {
                return;
            }
            newSelected.push(rate);
            //If a rate is selected, we check if every other rates of its group are checked to check the group as well
            if (rate.group) {
                //Check if the number of rates selected with the current rate group is equal to the number of every rates with this group
                let selectedRatesLength = newSelected.filter(frate => frate.group === rate.group).length;
                let ratesLength = rates.filter(frate => frate.group === rate.group).length;
                if (selectedRatesLength === ratesLength) {
                    let tmpGroup = groups.find(group => group.id === rate.group);
                    let newSelectedGroups = Array.from(selectedGroups);
                    newSelectedGroups.push(tmpGroup);
                    setSelectedGroups(newSelectedGroups);
                }
            }

            if ((rate.group || selectedGroups.length === groups.length) && newSelected.length === rates.length && rates.length > 0) {
                setAllSelected(true);
            }
            return setSelected(newSelected);
        },
        [selectedRates, selectedGroups, groups]
    );

    const deselectRate = React.useCallback(
        rate => {
            if (allSelected) {
                setAllSelected(false);
            }
            let newSelected = selectedRates.slice(0);
            let selectedIndex = selectedRates.findIndex(selement => selement.id === rate.id);
            newSelected.splice(selectedIndex, 1);

            //If a rate is unchecked, we look if its group is checked, we uncheck it
            if (rate.group) {
                //Check if the number of rates selected with the current rate group is equal to the number of every rates with this group
                let selectedRatesLength = newSelected.filter(frate => frate.group === rate.group).length + 1;
                let ratesLength = rates.filter(frate => frate.group === rate.group).length;
                if (selectedRatesLength === ratesLength) {
                    let newSelectedGroups = selectedGroups.slice(0);
                    let selectedIndexGroup = selectedGroups.findIndex(sgroup => sgroup.id === rate.group);
                    newSelectedGroups.splice(selectedIndexGroup, 1);
                    setSelectedGroups(newSelectedGroups);
                }
            }
            return setSelected(newSelected);
        },
        [allSelected, selectedRates, rates, selectedGroups]
    );

    const selectGroup = React.useCallback(
        group => {
            let newSelected = selectedGroups.slice(0);
            if (newSelected.find(selected => selected.id === group.id)) {
                return;
            }
            newSelected.push(group);
            // we check every rates of its group
            let ratesFromGroup = rates.filter(rate => rate.group === group.id);
            let tmpSelectedRates = Array.from(selectedRates);
            tmpSelectedRates.push(...ratesFromGroup);
            tmpSelectedRates = Array.from(new Set(tmpSelectedRates));
            setSelected(tmpSelectedRates);
            if (newSelected.length === groups.length && tmpSelectedRates.length === rates.length && rates.length > 0) {
                setAllSelected(true);
            }
            return setSelectedGroups(newSelected);
        },
        [selectedGroups, rates, selectedRates, groups]
    );

    const deselectGroup = React.useCallback(
        group => {
            if (allSelected) {
                setAllSelected(false);
            }
            let newSelected = selectedGroups.slice(0);
            let selectedIndex = selectedGroups.findIndex(selement => selement.id === group.id);
            newSelected.splice(selectedIndex, 1);
            //We uncheck every rate of the group
            let ratesFromGroup = rates.filter(rate => rate.group === group.id);
            ratesFromGroup = ratesFromGroup.map(rate => rate.id);
            let tmpSelectedRates = selectedRates.filter(rate => !ratesFromGroup.includes(rate.id));
            setSelected(tmpSelectedRates);
            return setSelectedGroups(newSelected);
        },
        [allSelected, selectedGroups, rates, selectedRates]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            setAllSelected(!allSelected);
            if (checked) {
                setSelected([...rates]);
                setSelectedGroups([...groups]);
            } else {
                setSelected([]);
                setSelectedGroups([]);
            }
        },
        [allSelected, rates, groups]
    );

    // Handle click./check with shift key
    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, element, type) => {
            event.stopPropagation();
            let tmpArray = type === 'rate' ? selectedRates : selectedGroups;
            const selected = tmpArray.findIndex(selement => selement.id === element.id) > -1;
            if ((selected && checked) || (!selected && !checked)) {
                return;
            }

            let newSelected = tmpArray.slice(0);

            if (checked && !selected) {
                if (event.shiftKey) {
                    let [lastSelected] = tmpArray.slice(-1);
                    let lastSelectedIndex = tmpArray.findIndex(felement => felement.id === lastSelected.id);
                    let selectionIndex = tmpArray.findIndex(felement => felement.id === element.id);

                    if (selectionIndex < lastSelectedIndex) {
                        [selectionIndex, lastSelectedIndex] = [lastSelectedIndex, selectionIndex];
                    }

                    newSelected.push(...selectedRates.slice(lastSelectedIndex, selectionIndex + 1));

                    newSelected = Array.from(new Set(newSelected));
                } else {
                    return type === 'rate' ? selectRate(element) : selectGroup(element);
                }
            }

            if (!checked && selected) {
                return type === 'rate' ? deselectRate(element) : deselectGroup(element);
            }
            return type === 'rate' ? setSelected(newSelected) : setSelectedGroups(newSelected);
        },
        [selectedRates, selectedGroups, rates]
    );

    const updateFilters = React.useCallback(
        _filters => {
            //when loading the page if the default filter (here no filters) is identical to the
            // one that already exists in the state filters, we do not want to make an additional request
            if (!Object.keys(_filters).length && !Object.keys(filters).length) return;
            setFilters(_filters);
            reloadRates(inputSearchValue, _filters);
        },
        [reloadRates, inputSearchValue, filters]
    );

    const withFilters = React.useMemo(() => {
        return inputSearchValue !== '' || (Object.keys(filters).length !== 0 && Object.values(filters).some(val => val.size > 0 || typeof val === 'boolean'));
    }, [inputSearchValue, filters]);

    const optionsMode = [
        {
            key: 'visible',
            name: 'false',
            text: t('ticket.rates.filters.select_visible')
        },
        {
            key: 'invisible',
            name: 'true',
            text: t('ticket.rates.filters.select_invisible')
        }
    ];

    const optionsGroup = React.useMemo(() => {
        if (!groups) return [];
        return groups.map(group => {
            return {
                key: group.id,
                name: String(group.id),
                text: group.name
            };
        });
    }, [groups]);

    const optionsInventories = React.useMemo(() => {
        if (!inventories) return [];
        return inventories.map(inventory => {
            return {
                key: inventory.uuid,
                name: inventory.uuid,
                text: inventory.name
            };
        });
    }, [inventories]);

    const optionsCategories = React.useMemo(() => {
        if (!categories) return [];
        return categories.map(category => {
            return {
                key: category.uuid,
                name: category.uuid,
                text: category.name
            };
        });
    }, [categories]);

    const optionsContingents = React.useMemo(() => {
        if (!contingents) return [];
        return contingents.map(contingent => {
            return {
                key: contingent.uuid,
                name: contingent.uuid,
                text: contingent.name
            };
        });
    }, [contingents]);

    const getQuotaTakenRate = React.useCallback(
        rate => {
            return quotaTakenByRate[rate.id] || 0;
        },
        [quotaTakenByRate]
    );

    const allowSelectionDeletion = React.useMemo(() => {
        if (!selectedRates.length) {
            return true;
        }
        return !selectedRates.some(rate => getQuotaTakenRate(rate) || rate.has_active_tickets);
    }, [selectedRates, getQuotaTakenRate]);

    const renderFilters = React.useMemo(() => {
        // For some reasons React sometimes returns null for the boolean expressions below, thus displaying filters that breaks the page
        // With this Memo we ensure that every boolean expression is evaluated as such, and that render is conditioned to each options within the conditions list
        const filtersConditions = [
            { index: 1, name: 'visibility', label: 'visibility', value: Boolean(!['ticket_office', 'rates_stats'].includes(mode)), options: optionsMode, multiple: false },
            { index: 2, name: 'groups', label: 'group', value: Boolean(groups.length), options: optionsGroup, multiple: true },
            { index: 3, name: 'inventories', label: 'inventory', value: Boolean(isSeated && inventories.length), options: optionsInventories, multiple: true },
            { index: 4, name: 'categories', label: 'category', value: Boolean(isSeated && categories.length), options: optionsCategories, multiple: true },
            { index: 5, name: 'contingents', label: 'contingent', value: Boolean(isSeated && contingents.length), options: optionsContingents, multiple: true }
        ];

        // Prevent FilterSetController from rendering an empty list if none of the conditions above returns true
        if (filtersConditions.every(cdn => cdn && !cdn.value)) {
            return null;
        }

        // Iterate over conditions and render conditionnaly according to boolean values
        return filtersConditions
            .filter(cdn => cdn && cdn.value)
            .map(filter => {
                return (
                    <Filter
                        name={filter.name}
                        labelName={t(`ticket.rates.filters.label_${filter.label}`)}
                        multiple={filter.multiple}
                        options={filter.options}
                        index={filter.index}
                        key={filter.index}
                        labelCTA={t('ticket.common.action.apply')}
                        labelSelectAll={t('ticket.common.action.select-all')}
                    />
                );
            });
    }, [isSeated, mode, groups, categories, inventories, contingents, optionsMode, optionsGroup, optionsCategories, optionsInventories, optionsContingents]);

    if (isLoading) {
        return (
            <div style={{ height: '30vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader active>{t('ticket.common.loading')}</Loader>
            </div>
        );
    }

    return (
        <>
            <Card>
                <div className={mode === 'rate-list' ? Style['wrapper-table-rate-list'] : ''}>
                    <FilterSetController
                        onOpen={() => track('ACTION > FILTER', { 'Page Title': 'TICKET TYPES' })}
                        onChange={values => updateFilters(values)}
                        initialFilters={filters}
                        search={{
                            onChange: (synth, value) => {
                                setInputSearchValue(value);
                                reloadRates(value, filters);
                            },
                            handleReset: (synth, value) => {
                                setInputSearchValue('');
                                reloadRates('', filters);
                            },
                            placeholder: t('ticket.rates.filters.placeholder_search'),
                            value: inputSearchValue
                        }}
                        labelReset={t('ticket.common.action.reset')}
                        total={rates.length || 0}
                        labelTotal={t('ticket.rates.filters.label_total_rates', { count: rates.length || 0 })}
                    >
                        {renderFilters}
                    </FilterSetController>
                    {mode === 'rate-list' && (
                        <>
                            <div className={'container-global-actions'}>
                                <SelectButton
                                    onOpen={() => {
                                        track('ACTION > ACTION ON SELECTION', { 'Page Title': 'TICKET TYPES' });
                                    }}
                                    tooltipTitle={t('ticket.rates.noActionTooltip')}
                                    title={selectedRates.length > 0 ? t('ticket.rates.rateAction', { count: selectedRates.length }) : t('ticket.common.action.bulk')}
                                    objects={selectedRates}
                                >
                                    <SeeAttendeesAction model={RateModel} filterKey={event && event.is_seated ? 'booking__rate__in' : 'rate__in'} />
                                    <ModifyAction onClick={() => track('ACTION > EDIT RATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Multiple' })} />
                                    <GroupAction
                                        groups={groups}
                                        rates={rates}
                                        model={RateModel}
                                        onSave={() => {
                                            reloadAndResetSelected();
                                            track('ACTION > MOVE TO GROUP', { 'Page Title': 'TICKET TYPES', 'Action type': 'Multiple' });
                                        }}
                                    />
                                    {isSeated && <ContingentAction contingents={contingents} onSave={reloadAndResetSelected} />}
                                    {isSeated && <InventoryAction inventories={inventories} onSave={reloadAndResetSelected} />}
                                    {isSeated && <CategoryAction categories={categories} model={RateModel} onSave={reloadAndResetSelected} />}
                                    <Dropdown.Line />
                                    <DuplicateAction
                                        onSave={() => {
                                            reloadAndResetSelected();
                                            track('ACTION > DUPLICATE RATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Multiple' });
                                        }}
                                        rates={rates}
                                        groups={groups}
                                    />
                                    <GenerateTemplateFromRateAction
                                        onSave={() => {
                                            reloadAndResetSelected();
                                            track('ACTION > CREATE RATE TEMPLATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Multiple' });
                                        }}
                                    />
                                    <ModifyTemplateAction />
                                    {selectedRates.length === 1 ? <Dropdown.Line /> : null}
                                    <DeleteAction
                                        objects={selectedRates}
                                        rates={rates}
                                        model={RateModel}
                                        disabled={!allowSelectionDeletion}
                                        onSave={() => {
                                            reloadAndResetSelected();
                                            track('ACTION > DELETE RATE', { 'Page Title': 'TICKET TYPES', 'Action type': 'Multiple' });
                                        }}
                                    />
                                </SelectButton>
                                <div className={'container-secondary-actions'}>
                                    {(Boolean(rates.length) || Boolean(groups.length)) && (
                                        <SortRatesButton
                                            disabled={withFilters}
                                            onClick={() => {
                                                setOpenModalSort(true);
                                                track('ACTION > ORGANIZE RATES', { 'Page Title': 'TICKET TYPES' });
                                            }}
                                            label={t('ticket.rates.sortRates.actionLabel')}
                                        />
                                    )}
                                    <SwitcherAllGroups groups={groups} onChangeGroups={setGroups} />
                                </div>
                            </div>
                            {openModalSort && (Boolean(rates.length) || Boolean(groups.length)) && (
                                <ModalSortRates open={openModalSort} onClose={() => setOpenModalSort(false)} rates={rates} groups={groups} reloadList={reloadList} />
                            )}
                        </>
                    )}
                </div>

                {mode !== 'rate-list' && children}
                <div style={{ overflowX: 'auto' }}>
                    <RatesTable
                        t={t}
                        hideEmptyGroup={withFilters || hideEmptyGroup}
                        tableCells={tableCells}
                        event={event}
                        organizationId={organizationId}
                        groups={groups}
                        rates={rates}
                        GroupClass={RateGroup}
                        RateClass={RateModel}
                        categories={categories}
                        inventories={inventories}
                        reloadList={reloadList}
                        handleElementsList={handleElementsList}
                        selectedGroups={selectedGroups}
                        selectedRates={selectedRates}
                        allSelected={allSelected}
                        handleAllClick={(event, data) =>
                            handleAllClick({
                                event,
                                data
                            })
                        }
                        onToggleGroup={setGroups}
                        mode={mode}
                        selectable={selectable}
                        isLoading={isTableLoading}
                        getQuotaTakenRate={getQuotaTakenRate}
                        quotaTakenByGroup={quotaTakenByGroup}
                        settings={settings}
                    />
                </div>
            </Card>
        </>
    );
}

RatesManager.propTypes = {
    event: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    initialQuery: PropTypes.object.isRequired,
    tableCells: PropTypes.array.isRequired,
    queryParams: PropTypes.object,
    extraRates: PropTypes.array,
    excludeRatesIds: PropTypes.array,
    onGroupsLoaded: PropTypes.func,
    onRatesLoaded: PropTypes.func,
    onReloadList: PropTypes.func,
    selected: PropTypes.array,
    onRatesSelected: PropTypes.func,
    mode: PropTypes.string,
    hideEmptyGroup: PropTypes.bool,
    children: PropTypes.node,
    selectable: PropTypes.bool,
    quotaTakenByGroup: PropTypes.object,
    settings: PropTypes.object
};
